import type { HelpItem } from '../types';

export const helpContent: Record<string, HelpItem> = {
  'draft-fwd-port': {
    id: 'draft-fwd-port',
    title: 'Forward Port Draft',
    definition: 'The vertical distance from the bottom of the keel to the waterline on the port (left) side at the forward perpendicular.',
    howTo: 'Read the draft mark at the bow on the port side. If the water is between two marks, estimate to the nearest centimeter. Record in meters.',
    imageUrl: '/images/e4e1deb272af6a5639d61e1592c4daa34d987462.jpg',
    related: ['draft-fwd-stbd', 'trim']
  },
  'draft-fwd-stbd': {
    id: 'draft-fwd-stbd',
    title: 'Forward Starboard Draft',
    definition: 'The vertical distance from the bottom of the keel to the waterline on the starboard (right) side at the forward perpendicular.',
    howTo: 'Read the draft mark at the bow on the starboard side. Ensure the vessel is stationary and no waves distort the reading.',
    imageUrl: '/images/ca8cc67df785015cae4c9b31bd8414c25bb91045.png',
    related: ['draft-fwd-port', 'trim']
  },
  'draft-mid-port': {
    id: 'draft-mid-port',
    title: 'Midship Port Draft',
    definition: 'The draft reading taken amidships (middle of the vessel) on the port side. This is the most important reading for displacement calculation.',
    howTo: 'Locate the midship draft marks (usually indicated by a circle or diamond symbol). Read the port side carefully.',
    imageUrl: '/images/e4e1deb272af6a5639d61e1592c4daa34d987462.jpg',
    related: ['draft-mid-stbd', 'mean-of-means']
  },
  'draft-mid-stbd': {
    id: 'draft-mid-stbd',
    title: 'Midship Starboard Draft',
    definition: 'The draft reading taken amidships on the starboard side.',
    howTo: 'Read the starboard midship draft mark. If no midship marks exist, measure from the deck to waterline and subtract freeboard from ship plans.',
    imageUrl: '/images/e4e1deb272af6a5639d61e1592c4daa34d987462.jpg',
    related: ['draft-mid-port', 'mean-of-means']
  },
  'draft-aft-port': {
    id: 'draft-aft-port',
    title: 'Aft Port Draft',
    definition: 'The draft at the stern on the port side.',
    howTo: 'Read the draft mark near the rudder post on the port side. Aft drafts are often affected by propeller wash — read when engines are stopped.',
    imageUrl: '/images/e4e1deb272af6a5639d61e1592c4daa34d987462.jpg',
    related: ['draft-aft-stbd', 'trim']
  },
  'draft-aft-stbd': {
    id: 'draft-aft-stbd',
    title: 'Aft Starboard Draft',
    definition: 'The draft at the stern on the starboard side.',
    howTo: 'Read the draft mark near the stern on the starboard side.',
    imageUrl: '/images/e4e1deb272af6a5639d61e1592c4daa34d987462.jpg',
    related: ['draft-aft-port', 'trim']
  },
  'density': {
    id: 'density',
    title: 'Water Density (Specific Gravity)',
    definition: 'The mass per unit volume of the water the vessel is floating in. Standard seawater is 1.025 t/m³. Fresh water is 1.000.',
    howTo: 'Collect a water sample from the vessel side at mid-draft depth. Use a hydrometer or density meter. Record to 3 decimal places.',
    imageUrl: '/images/2c357899c4594a916c9a984bb53adcb070316532.png',
    related: ['displacement-correction']
  },
  'lbp': {
    id: 'lbp',
    title: 'Length Between Perpendiculars (LBP)',
    definition: 'The horizontal distance between the forward perpendicular (FP) and the aft perpendicular (AP). Used for trim corrections.',
    howTo: 'Found in the vessel Trim & Stability Booklet or General Arrangement plan. Do not confuse with LOA (Length Over All).',
    imageUrl: '/images/75f3666433be9b1277f82dd3dca963589b4078ba.jpg',
    related: ['first-correction', 'second-correction']
  },
  'trim': {
    id: 'trim',
    title: 'Trim',
    definition: 'The difference between the aft and forward mean drafts. Positive = trimmed by stern (aft deeper). Negative = trimmed by bow.',
    howTo: 'Calculated automatically: Trim = Aft Mean - Forward Mean. If trim exceeds 1% of LBP, apply second correction.',
    imageUrl: '/images/1c8c96158bfa34d2ef9bc9e2f524548830fc224f.jpg',
    related: ['first-correction', 'second-correction', 'lbp']
  },
  'first-correction': {
    id: 'first-correction',
    title: 'First Trim Correction',
    definition: 'Correction to the mean draft to account for the vessel trimming. Formula: (Trim[cm] x LCF x TPC) / LBP. Brings draft to LCF position.',
    howTo: 'Applied automatically by the calculator. LCF is taken from the hydrostatic table at the mean draft. Sign is naturally determined by LCF position relative to midship and trim direction.',
    imageUrl: '/images/1c8c96158bfa34d2ef9bc9e2f524548830fc224f.jpg',
    related: ['trim', 'lbp', 'second-correction']
  },
  'second-correction': {
    id: 'second-correction',
    title: 'Second Trim Correction',
    definition: 'A correction for the change in MTC (Moment to Change Trim) over the draft range. Formula: (Trim² x 50 x δMTC) / LBP. ALWAYS POSITIVE.',
    howTo: 'Applied automatically when trim is significant. Uses δMTC = MTC at (draft+50cm) - MTC at (draft-50cm). Always added to displacement.',
    imageUrl: '/images/75f3666433be9b1277f82dd3dca963589b4078ba.jpg',
    related: ['trim', 'first-correction', 'mtc']
  },
  'list-corr': {
    id: 'list-corr',
    title: 'List / Heel Correction',
    definition: 'Correction for the vessel listing (tilting to one side). Formula: 6 x (TPCdeep - TPCshallow) x (Draftdeep - Draftshallow). ALWAYS POSITIVE.',
    howTo: 'Applied automatically when list is detected. Uses TPC values interpolated at the deep and shallow midship drafts. Always added to displacement.',
    imageUrl: '/images/e4e1deb272af6a5639d61e1592c4daa34d987462.jpg',
    related: ['list', 'draft-mid-port', 'draft-mid-stbd']
  },
  'lcf': {
    id: 'lcf',
    title: 'Longitudinal Center of Flotation (LCF)',
    definition: 'The point about which the vessel trims/pivots. Distance from MIDSHIP. CRITICAL: Sign convention varies by shipyard!',
    howTo: 'European/American convention (used by this app): POSITIVE = AFT of midship, NEGATIVE = FORWARD of midship. Some Asian shipyards use the OPPOSITE. Check your stability book! If your table uses negative=aft, multiply all LCF values by -1 before uploading.',
    imageUrl: '/images/1c8c96158bfa34d2ef9bc9e2f524548830fc224f.jpg',
    related: ['first-correction', 'trim']
  },
  'mtc': {
    id: 'mtc',
    title: 'Moment to Change Trim (MTC)',
    definition: 'The moment required to change the vessel trim by 1 centimeter. Used in the second correction formula.',
    howTo: 'Found in the hydrostatic table. The calculator uses MTC at the mean draft plus/minus 50cm to compute δMTC for the second correction.',
    imageUrl: '/images/2c357899c4594a916c9a984bb53adcb070316532.png',
    related: ['second-correction', 'hydrostatic']
  },
  'ullage': {
    id: 'ullage',
    title: 'Ullage',
    definition: 'The distance from the top of the tank (datum point) to the surface of the liquid. Used for tankers and LPG.',
    howTo: 'Open the ullage port. Lower the ullage tape until the bob touches the liquid. Read the distance from the tape hook to the liquid surface.',
    imageUrl: '/images/cf477366e2a8d1e34ca4fd91860ccd730ed81bd1.jpg',
    related: ['sounding', 'tank-table', 'ullage-tape']
  },
  'ullage-tape': {
    id: 'ullage-tape',
    title: 'Ullage Measuring Tape',
    definition: 'Specialized tape measure with a weighted bob used to measure ullage or sounding in cargo tanks.',
    howTo: 'Lower the tape through the ullage port until the bob touches the liquid. The reading is taken at the reference point on the deck.',
    imageUrl: '/images/6422f1f56c6cdff97ac0b2e3193730643dd7d4ae.jpg',
    related: ['ullage', 'sounding']
  },
  'sounding': {
    id: 'sounding',
    title: 'Sounding',
    definition: 'The depth of liquid in the tank measured from the bottom of the tank to the liquid surface.',
    howTo: 'Lower the sounding tape until it touches the tank bottom. Read the wet length. Some tanks use sounding instead of ullage.',
    imageUrl: '/images/cf477366e2a8d1e34ca4fd91860ccd730ed81bd1.jpg',
    related: ['ullage', 'tank-table']
  },
  'tank-temp': {
    id: 'tank-temp',
    title: 'Tank Temperature',
    definition: 'The temperature of the cargo in the tank, used for volume correction factor (VCF) calculation.',
    howTo: 'Use a portable thermometer or fixed tank temperature gauge. Record in °C. For tankers, take temperature at mid-depth.',
    imageUrl: '/images/945a0b7db8f3f653ba376fbc5bf93de5ee5eddd9.jpg',
    related: ['vcf', 'density', 'ullage']
  },
  'observed-density': {
    id: 'observed-density',
    title: 'Observed Density',
    definition: 'The density of the cargo at the observed temperature, measured with a hydrometer or from lab analysis.',
    howTo: 'Take a representative sample from the tank. Measure density at the observed temperature using a hydrometer. For LPG, use a pressurized sampling device.',
    imageUrl: '/images/2bfa6bfc8c9dc0f9860ecc1acc58cf6d206be217.png',
    related: ['tank-temp', 'vcf', 'ullage']
  },
  'lpg-temp': {
    id: 'lpg-temp',
    title: 'LPG Temperature',
    definition: 'The temperature of liquefied petroleum gas in the tank. Fully refrigerated LPG is typically carried at -42°C to -50°C.',
    howTo: 'Read from the ship tank temperature gauges. LPG density changes significantly with temperature — accuracy is critical.',
    imageUrl: '/images/4b353193cc7e68f40d1d1e1b9d6f448c5dd44600.png',
    related: ['lpg-density', 'ullage']
  },
  'lpg-density': {
    id: 'lpg-density',
    title: 'LPG Density',
    definition: 'The mass per unit volume of LPG at the observed tank temperature and pressure. Varies significantly with temperature.',
    howTo: 'Use ship provided density tables or lab analysis. Propane ≈ 0.51 t/m³, Butane ≈ 0.58 t/m³ at standard conditions.',
    imageUrl: '/images/4b353193cc7e68f40d1d1e1b9d6f448c5dd44600.png',
    related: ['lpg-temp', 'ullage']
  },
  'constants': {
    id: 'constants',
    title: 'Vessel Constants',
    definition: 'Weights that remain on board but are not cargo: bunkers (fuel), fresh water, ballast, stores, slops, etc.',
    howTo: 'Read from ship records or sounding reports. Enter each value in metric tons. These are deducted to isolate cargo weight.',
    imageUrl: '/images/945a0b7db8f3f653ba376fbc5bf93de5ee5eddd9.jpg',
    related: ['bunkers', 'ballast', 'fw']
  },
  'bunkers': {
    id: 'bunkers',
    title: 'Bunkers (Fuel Oil)',
    definition: 'Fuel oil and diesel oil on board for the vessel engines and generators. Includes HFO, MDO, and lubricating oil.',
    howTo: 'Read from ship bunker reports or sounding tables. Sum all fuel types. Deduct from displacement to get cargo.',
    imageUrl: '/images/945a0b7db8f3f653ba376fbc5bf93de5ee5eddd9.jpg',
    related: ['constants', 'fw', 'ballast']
  },
  'ballast': {
    id: 'ballast',
    title: 'Ballast Water',
    definition: 'Seawater taken into ballast tanks for stability when vessel is not carrying cargo. Must be deducted from displacement.',
    howTo: 'Sound all ballast tanks. Use tank tables to convert soundings to volume, then multiply by water density (typically 1.025 t/m³).',
    imageUrl: '/images/945a0b7db8f3f653ba376fbc5bf93de5ee5eddd9.jpg',
    related: ['constants', 'bunkers']
  },
  'fw': {
    id: 'fw',
    title: 'Fresh Water',
    definition: 'Potable water stored on board for crew consumption and technical use.',
    howTo: 'Read from ship fresh water tank soundings or daily reports. Enter in metric tons.',
    imageUrl: '/images/945a0b7db8f3f653ba376fbc5bf93de5ee5eddd9.jpg',
    related: ['constants', 'bunkers', 'ballast']
  },
  'stores': {
    id: 'stores',
    title: 'Stores & Provisions',
    definition: 'Food, spare parts, lubricants, and other supplies on board for the vessel operation.',
    howTo: 'Estimate from ship records. Usually a small fixed amount. Can often be combined with "others".',
    imageUrl: '/images/945a0b7db8f3f653ba376fbc5bf93de5ee5eddd9.jpg',
    related: ['constants']
  },
  'slops': {
    id: 'slops',
    title: 'Slops',
    definition: 'Residual oily water mixture in slop tanks, typically on tankers. Must be accounted for as constant weight.',
    howTo: 'Measure slop tank contents via ullage/sounding. Use tank table and density to calculate weight.',
    imageUrl: '/images/cf477366e2a8d1e34ca4fd91860ccd730ed81bd1.jpg',
    related: ['constants', 'ullage']
  },
  'hydrostatic': {
    id: 'hydrostatic',
    title: 'Hydrostatic Table',
    definition: 'A table provided by the shipyard showing displacement, TPC, MTC, and LCF values for various drafts.',
    howTo: 'Found in the vessel Trim & Stability Booklet. Upload as CSV/JSON in the vessel manager. CRITICAL: Check LCF sign convention in your stability book. European convention: positive = AFT of midship. Asian convention: negative = AFT of midship. This app uses European convention.',
    imageUrl: '/images/2c357899c4594a916c9a984bb53adcb070316532.png',
    related: ['displacement', 'lbp', 'lcf', 'mtc']
  },
  'tank-table': {
    id: 'tank-table',
    title: 'Tank Capacity Table',
    definition: 'A table showing the relationship between ullage/sounding and volume for each cargo tank.',
    howTo: 'Provided by shipyard or tank manufacturer. Upload per tank in the vessel manager. Required for ullage and LPG methods.',
    imageUrl: '/images/cf477366e2a8d1e34ca4fd91860ccd730ed81bd1.jpg',
    related: ['ullage', 'sounding', 'lpg']
  }
,
  'lcf-sign-convention': {
    id: 'lcf-sign-convention',
    title: 'LCF Sign Convention (Critical)',
    definition: 'The sign convention for LCF (Longitudinal Center of Flotation) varies by shipyard and region. Using the wrong sign will produce incorrect trim corrections and cargo weights.',
    howTo: 'EUROPEAN/AMERICAN convention (used by this app): POSITIVE (+) = AFT of midship, NEGATIVE (-) = FORWARD of midship. ASIAN convention (some Japanese/Korean shipyards): NEGATIVE (-) = AFT of midship, POSITIVE (+) = FORWARD of midship. CHECK YOUR STABILITY BOOK! If your table uses negative=aft, multiply all LCF values by -1 before entering them in this app.',
    imageUrl: '/images/1c8c96158bfa34d2ef9bc9e2f524548830fc224f.jpg',
    related: ['lcf', 'first-correction', 'trim']
  },
  'hydrometer-types': {
    id: 'hydrometer-types',
    title: 'Hydrometer Types (Draft vs Load Line)',
    definition: 'Two different hydrometers are used in maritime surveys. Draft survey hydrometers measure APPARENT DENSITY (weight in air per unit volume, kg/l). Load line hydrometers measure RELATIVE DENSITY (specific gravity, ratio to distilled water).',
    howTo: 'For DRAFT SURVEYS: Use a glass hydrometer marked "for draft survey" calibrated at 15°C (60°F), range 0.990-1.040 kg/l. It measures apparent density in air. For LOAD LINE surveys: Use a hydrometer marked "RD" or "Sp.Gr." measuring relative density. The difference between them is the "air buoyancy correction" (approx 0.002). NEVER use a load line hydrometer for draft survey calculations — it will give incorrect displacement values.',
    imageUrl: '/images/2bfa6bfc8c9dc0f9860ecc1acc58cf6d206be217.png',
    related: ['density', 'displacement-correction']
  },
  'manometer': {
    id: 'manometer',
    title: 'Manometer (Alternative Draft Reading)',
    definition: 'A manometer is a U-tube device used to measure the list (heel) of a vessel when the offside draft marks cannot be safely accessed due to weather or sea conditions.',
    howTo: 'Construct from 35-40m of 10mm plastic tubing filled with water, with valves at each end. Place one end on the port side deck, one on starboard. Measure the water height difference with a tape measure. The starboard draft = port draft + difference. If the manometer is shorter than the vessel beam, use similar triangles: Difference in drafts = (Vessel Beam / Distance between manometer readings) × Measured difference.',
    imageUrl: '/images/1c8c96158bfa34d2ef9bc9e2f524548830fc224f.jpg',
    related: ['draft-fwd-port', 'draft-fwd-stbd', 'list-corr']
  },
  'ballast-warning': {
    id: 'ballast-warning',
    title: 'Ballast Water Measurement Warning',
    definition: 'Ballast water is the LARGEST source of error in draft surveys. Inaccurate sounding, density, or tank table readings can produce cargo weight errors of hundreds of tonnes.',
    howTo: '1. Sound ALL ballast tanks including peaks and voids. 2. Use water-finding paste (NOT chalk). 3. Verify tanks are either fully pressed up or completely empty. 4. Take density samples from each tank if filled from different sources. 5. Check for mud/silt in tanks — this affects density. 6. Verify duct keels and lower stools are dry. If in doubt, re-sound. A 1cm error in a large ballast tank can mean 50+ tonnes error in final cargo weight.',
    imageUrl: '/images/945a0b7db8f3f653ba376fbc5bf93de5ee5eddd9.jpg',
    related: ['ballast', 'constants', 'sounding']
  },
  'weather-conditions': {
    id: 'weather-conditions',
    title: 'Weather & Sea Conditions',
    definition: 'Weather and sea state significantly affect draft reading accuracy. Wave, swell, wind, and current can all introduce errors.',
    howTo: 'Ideal conditions: calm water, no wind, slack tide. For exposed berths: take 12 readings, discard highest and lowest, average the remaining 10. Use a settling tube (clear plastic tube with restrictions) at midships to dampen swell. Read drafts during slack water periods. Record wind force, sea state, and current direction on the survey report. If conditions exceed safe limits, postpone the survey.',
    imageUrl: '/images/1c8c96158bfa34d2ef9bc9e2f524548830fc224f.jpg',
    related: ['draft-fwd-port', 'draft-mid-port', 'draft-aft-port']
  },
  'equipment-checklist': {
    id: 'equipment-checklist',
    title: 'Survey Equipment Checklist',
    definition: 'Proper equipment is essential for accurate draft surveys. Missing or uncalibrated tools are a common source of error.',
    howTo: 'Required: Strong torch, boat/ladder for offside access, binoculars, patent draft indicator or measuring tube, calibrated inclinometer or manometer, steel tape with brass plumb bob (calibrated), seawater sampling bucket, calibrated draft survey hydrometer (glass, 0.990-1.040 kg/l), salinity refractometer, ballast water sampling device, waterproof notebook, water-finding paste. Optional: Settling tube for swell conditions, camera for documentation.',
    imageUrl: '/images/6422f1f56c6cdff97ac0b2e3193730643dd7d4ae.jpg',
    related: ['manometer', 'hydrometer-types', 'density']
  }};

export function getHelp(id: string): HelpItem | undefined {
  return helpContent[id];
}
